import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import TitleRow from '@/components/ui/TitleRow'
import {
  AverageBodySkeleton,
  AverageToolbarSkeleton,
} from '@/features/trading-average/components/AverageSkeleton'
import TradingAveragePage from '@/features/trading-average/components/TradingAveragePage'
import { toTradingSearch, tradingSearchSchema } from '@/features/trading-history/lib/tradingSearch'
import { loadTradingAverage } from '@/screens/market/TradingAverageScreen.loader'

/** Known without data, so the pending and failed pages draw the real one. */
const HEADER = <TitleRow title="Trading Average" />

/** The mock stacks the strip, the filters, the cards, the bars and the table 16 apart, not the page's 24. */
const STACK = 'gap-4'

function TradingAverageScreenPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading trading average…"
      header={HEADER}
      pageTitle="Trading Average"
      mainClassName={STACK}
    >
      <AverageToolbarSkeleton />
      <AverageBodySkeleton />
    </ScreenLayout>
  )
}

function TradingAverageScreenError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} header={HEADER} title="Couldn't load trading average" />
}

export const Route = createFileRoute('/_app/market/trading-average')({
  validateSearch: tradingSearchSchema,
  loader: loadTradingAverage,
  pendingComponent: TradingAverageScreenPending,
  errorComponent: TradingAverageScreenError,
  component: TradingAverageScreen,
})

function TradingAverageScreen() {
  // Re-parsed, not read straight off `Route.useSearch()`: the router merges the
  // raw query string into it, so a stray param would key the read off a
  // different object than the one the loader warmed.
  const search = toTradingSearch(Route.useSearch())
  const navigate = Route.useNavigate()

  return (
    <ScreenLayout header={HEADER} pageTitle="Trading Average" mainClassName={STACK}>
      <TradingAveragePage
        search={search}
        onSearchChange={(patch) => {
          void navigate({ search: (prev) => ({ ...prev, ...patch }) })
        }}
      />
    </ScreenLayout>
  )
}
